import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { LoaderSymbol } from '../../components/loading';

/**
 * Settings → Reporting → MSR → MSR Data Import Config — New / Edit detail dialog
 * (ALPACA-LIMS `MSRDataImportConfiguration` parity).  One config row per water
 * system tells the Monthly Summary Report how to pull a month's samples.
 *
 * The five id-lists (Sample Type(s) / Client / Project / PWS ID / Sample Site)
 * are type-to-search multi-select GUID pickers — they can't be expressed as a
 * generic MODULE_CRUD field list, so ModuleListView special-cases the
 * `msr-data-import-config` key to open THIS overlay instead of the shared
 * full-page SettingsDetailPage (same pattern as the Monthly Summary Repository
 * `<MsrDetailDialog>`).  Options come from the ready backend lookup endpoint
 * `GET /api/v1/msr-data-import-config/options/{kind}` (q + ids); the System
 * dropdown from `GET /api/v1/msr-water-system/options`.
 *
 * `MSRCategory` is server-derived from the chosen System (= its SystemName), so
 * it's shown read-only.  `ImportMode` is the mutually-exclusive Stored Procedure
 * / Excel Import choice.  Save runs through the shared `useSaveFlow`
 * ("Do you want to Save?" → POST/PUT → "Saved successfully." → back to list;
 * Edit gates the audit reason, Create is exempt).  Buttons/glyphs/tokens follow
 * CLAUDE.md §18.6 (shared `.ca-btn` family, `.qs-*` chrome, `--ai-*` tokens).
 * (Follow-up slice to the 2026-07-02 MSR merge.)
 */
interface Props {
  /** null = New; an Oid = Edit (form prefilled from GET {id}). */
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface MsrOption { id: string; name: string }

/** The five multi-select lookups → their backend `options/{kind}` route. */
const LOOKUPS: { field: LookupField; kind: string; label: string }[] = [
  { field: 'sampleTypes', kind: 'sample-types', label: 'Sample Type(s)' },
  { field: 'client',      kind: 'clients',      label: 'Client' },
  { field: 'project',     kind: 'projects',     label: 'Project' },
  { field: 'pwsId',       kind: 'pws-ids',      label: 'PWS ID' },
  { field: 'sampleSite',  kind: 'sample-sites', label: 'Sample Site' },
];
type LookupField = 'sampleTypes' | 'client' | 'project' | 'pwsId' | 'sampleSite';

// The five ';'-joined GUID-list fields, kept lowercase (the options endpoint
// serialises Guids lowercase; GUIDs are case-insensitive in SQL so the saved
// value round-trips either way — lowercasing just keeps the checkbox state and
// the <select> value matching, same fix MsrDetailDialog applies to Category).
type Lists = Record<LookupField, string>;
const EMPTY_LISTS: Lists = { sampleTypes: '', client: '', project: '', pwsId: '', sampleSite: '' };

export function MsrDataImportConfigDialog({ id, onClose, onSaved }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const editing = id != null;

  const [systems, setSystems] = useState<MsrOption[]>([]);
  const [system, setSystem] = useState('');            // MSRWaterSystem Oid (lowercase)
  const [msrCategory, setMsrCategory] = useState('');  // prefilled category (edit); preview = system name
  const [lists, setLists] = useState<Lists>(EMPTY_LISTS);
  const [importMode, setImportMode] = useState<'sp' | 'excel'>('sp');
  const [nullProject, setNullProject] = useState(false);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(editing);

  // System dropdown options (small reference list — load once).
  useEffect(() => {
    let alive = true;
    api.get<{ data?: MsrOption[] }>('/api/v1/msr-water-system/options', { silentLoading: true } as never)
      .then((r) => { if (alive) setSystems(r.data.data ?? []); })
      .catch(() => { if (alive) notify.error('Could not load Water Systems.'); });
    return () => { alive = false; };
  }, [notify]);

  // Edit: prefill the whole form from the config detail.
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    setLoading(true);
    api.get<{ data?: Record<string, unknown> }>(`/api/v1/msr-data-import-config/${id}`)
      .then((r) => {
        if (!alive) return;
        const d = r.data.data ?? {};
        setSystem(String(d.system ?? '').toLowerCase());
        setMsrCategory(String(d.msrCategory ?? ''));
        setLists({
          sampleTypes: String(d.sampleTypes ?? '').toLowerCase(),
          client:      String(d.client ?? '').toLowerCase(),
          project:     String(d.project ?? '').toLowerCase(),
          pwsId:       String(d.pwsId ?? '').toLowerCase(),
          sampleSite:  String(d.sampleSite ?? '').toLowerCase(),
        });
        setImportMode(String(d.importMode ?? 'sp') === 'excel' ? 'excel' : 'sp');
        setNullProject(Boolean(d.nullProject));
        setComment(String(d.comment ?? ''));
      })
      .catch(() => { if (alive) notify.error('Could not load this MSR Data Import Config.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [editing, id, notify]);

  // MSR Category preview: the chosen System's name (= what the server stores);
  // falls back to the prefilled value until the System list resolves.
  const categoryPreview =
    (system && systems.find((s) => s.id === system)?.name) || msrCategory || '';

  const setList = (field: LookupField, value: string) =>
    setLists((prev) => ({ ...prev, [field]: value }));

  // Save via the ONE shared flow (§18.6).  Server derives MSRCategory + the
  // SP/Excel bits, mints nothing (no sequential ID), audits Update per field.
  const save = async () => {
    if (!system) { notify.warning('Please select a Water System.'); return; }
    await runSave({
      formName: 'MSR Data Import Config',
      auditAction: editing ? 'edit' : 'create',
      save: async () => {
        const body = {
          system,
          sampleTypes: lists.sampleTypes || null,
          client:      lists.client || null,
          project:     lists.project || null,
          pwsId:       lists.pwsId || null,
          sampleSite:  lists.sampleSite || null,
          importMode,
          nullProject,
          comment: comment.trim() || null,
        };
        if (editing) await api.put(`/api/v1/msr-data-import-config/${id}`, body);
        else await api.post('/api/v1/msr-data-import-config', body);
      },
      onSaved,
    });
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, alignSelf: 'start' as const, paddingTop: 6 };

  return (
    <div className="qs-overlay" onMouseDown={onClose}>
      <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()} style={{ width: 720, maxWidth: '96vw' }}>
        <div className="qs-titlebar">
          <span>{editing ? 'Edit MSR Data Import Config' : 'New MSR Data Import Config'}</span>
          <div className="qs-titlebar-right">
            <i className="ti ti-x qs-close" title="Close" onClick={onClose} />
          </div>
        </div>

        <div className="qs-body" style={{ maxHeight: '78vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ai-muted)' }}>
              <LoaderSymbol size={22} /> Loading…
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center', maxWidth: 640 }}>
              {/* System (required) — drives the read-only MSR Category. */}
              <label style={labelStyle}>
                System <span style={{ color: 'var(--ai-danger, #d33)' }}>*</span>
              </label>
              <select className="qs-input" value={system} onChange={(e) => setSystem(e.target.value)} style={{ width: '100%' }}>
                <option value="">Select a Water System…</option>
                {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>

              {/* MSR Category — server-derived (= the System's name), read-only. */}
              <label style={labelStyle}>MSR Category</label>
              <input className="qs-input" value={categoryPreview} readOnly
                title="Set automatically from the chosen System"
                style={{ width: '100%', background: 'var(--ai-readonly, #f2f3f5)', color: 'var(--ai-muted)' }} />

              {/* Five type-to-search multi-select GUID pickers. */}
              {LOOKUPS.map((l) => (
                <MsrLookupMultiSelect key={l.field}
                  kind={l.kind} label={l.label}
                  value={lists[l.field]} onChange={(v) => setList(l.field, v)}
                  labelStyle={labelStyle} />
              ))}

              {/* Import Mode — mutually-exclusive SP / Excel. */}
              <label style={labelStyle}>Import Mode</label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="radio" name="msr-dic-mode" checked={importMode === 'sp'}
                    onChange={() => setImportMode('sp')} /> Stored Procedure
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="radio" name="msr-dic-mode" checked={importMode === 'excel'}
                    onChange={() => setImportMode('excel')} /> Excel Import
                </label>
              </div>

              {/* Null Project — include samples whose ProjectID is null. */}
              <label style={labelStyle}>Null Project</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={nullProject} onChange={(e) => setNullProject(e.target.checked)} />
                Include samples with no Project
              </label>

              {/* Comment. */}
              <label style={labelStyle}>Comment</label>
              <textarea className="qs-input" value={comment} rows={3}
                onChange={(e) => setComment(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
            </div>
          )}
        </div>

        <div className="qs-footer">
          <div className="qs-footer-actions">
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={loading}>
              <span className="tb-i">💾</span> Save
            </button>
            <button className="ca-btn" onClick={onClose}>
              <span className="tb-i tb-i-grey">↩︎</span> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Type-to-search multi-select over one MSR lookup.  Stores its selection as a
 * ';'-joined GUID string (legacy XAF CheckedListBoxEditor format).  Options load
 * lazily from `/api/v1/msr-data-import-config/options/{kind}` (server returns the
 * first 500 matching `q`, UNIONed with any `ids` so already-selected values keep
 * a label even outside the search window).  Popup portaled to <body> with the
 * shared `.qs-combo*` chrome so it looks identical to the Query Panel combos.
 */
function MsrLookupMultiSelect({ kind, label, value, onChange, labelStyle }: {
  kind: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  labelStyle: React.CSSProperties;
}) {
  const ids = useMemo(
    () => value.split(';').map((s) => s.trim()).filter(Boolean),
    [value],
  );
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState<MsrOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // Selection snapshot at open-time — sent as `ids` so the option list unions in
  // the currently-selected values, WITHOUT refetching on every checkbox toggle.
  const openIdsRef = useRef<string[]>([]);
  // Mirror of `labels` for the by-ids resolver effect (avoids a deps loop).
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const mergeLabels = (data: MsrOption[]) =>
    setLabels((prev) => {
      const n = { ...prev };
      data.forEach((o) => { n[o.id] = o.name; });
      return n;
    });

  // Resolve display names for already-selected ids (edit prefill / external
  // change) — only fetches the ids we don't already have a label for.
  useEffect(() => {
    const missing = ids.filter((x) => !labelsRef.current[x]);
    if (missing.length === 0) return;
    let alive = true;
    api.get<{ data?: MsrOption[] }>(`/api/v1/msr-data-import-config/options/${kind}`,
      { params: { ids: missing.join(',') }, silentLoading: true } as never)
      .then((r) => { if (alive) mergeLabels(r.data.data ?? []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [value, kind, ids]);

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Load options when the panel is open (debounced type-to-search).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get<{ data?: MsrOption[] }>(`/api/v1/msr-data-import-config/options/${kind}`,
      { params: { q: q || undefined, ids: openIdsRef.current.join(',') || undefined }, silentLoading: true } as never)
      .then((r) => {
        if (!alive) return;
        const data = r.data.data ?? [];
        setOpts(data);
        mergeLabels(data);
      })
      .catch(() => { if (alive) setOpts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, q, kind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const openPanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    openIdsRef.current = ids;
    setInput('');
    setQ('');
    setOpen((o) => !o);
  };

  const toggle = (o: MsrOption) => {
    const set = new Set(ids);
    if (set.has(o.id)) set.delete(o.id); else set.add(o.id);
    setLabels((prev) => ({ ...prev, [o.id]: o.name }));
    onChange([...set].join(';'));
  };

  const allSelected = opts.length > 0 && opts.every((o) => ids.includes(o.id));
  const selectAll = () => {
    mergeLabels(opts);
    onChange([...new Set([...ids, ...opts.map((o) => o.id)])].join(';'));
  };

  const summary = ids.length === 0 ? `Select ${label}…`
    : ids.length === 1 ? (labels[ids[0]!] ?? '1 selected')
    : `${ids.length} selected`;

  return (
    <>
      <label style={labelStyle}>{label}</label>
      <button ref={btnRef} type="button" className="qs-combo" onClick={openPanel} style={{ width: '100%' }}>
        <span className={ids.length ? 'qs-combo-val' : 'qs-combo-ph'}>{summary}</span>
        {ids.length
          ? <i className="ti ti-x qs-combo-clear" onClick={(e) => { e.stopPropagation(); onChange(''); }} />
          : <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="qs-combo-backdrop" onMouseDown={() => setOpen(false)} />
          <div className="qs-combo-pop" style={{ top: pos.top, left: pos.left, minWidth: Math.max(240, pos.width) }}>
            <div className="qs-combo-search">
              <i className="ti ti-search" />
              <input autoFocus value={input} placeholder="Type to filter…"
                onChange={(e) => setInput(e.target.value)} />
            </div>
            {!loading && opts.length > 0 && (
              <div className="qs-combo-tools">
                <button type="button" className="qs-combo-ftr-btn"
                  onClick={selectAll} disabled={allSelected}>Select all</button>
                <button type="button" className="qs-combo-ftr-btn red"
                  onClick={() => onChange('')} disabled={ids.length === 0}>Clear</button>
                <span style={{ flex: 1 }} />
                <button type="button" className="qs-combo-ftr-ok" onClick={() => setOpen(false)}>OK</button>
              </div>
            )}
            <div className="qs-combo-list">
              {loading && <div className="qs-combo-empty">Loading…</div>}
              {!loading && opts.length === 0 && <div className="qs-combo-empty">No values</div>}
              {opts.map((o) => (
                <label key={o.id} className={`qs-combo-opt${ids.includes(o.id) ? ' sel' : ''}`} title={o.name}>
                  <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o)} />
                  <span>{o.name}</span>
                </label>
              ))}
              {!loading && opts.length >= 500 && (
                <div className="qs-combo-empty">First 500 — type to refine</div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
